import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { customEventsApi } from '../../api/customEvents';

export const fetchEvents = createAsyncThunk(
  'customEvents/fetchEvents',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await customEventsApi.listEvents(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchEvent = createAsyncThunk(
  'customEvents/fetchEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      const response = await customEventsApi.getEvent(eventId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createEvent = createAsyncThunk(
  'customEvents/createEvent',
  async (eventData, { rejectWithValue }) => {
    try {
      const response = await customEventsApi.createEvent(eventData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateEvent = createAsyncThunk(
  'customEvents/updateEvent',
  async ({ eventId, updates }, { rejectWithValue }) => {
    try {
      const response = await customEventsApi.updateEvent(eventId, updates);
      return { eventId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'customEvents/deleteEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      await customEventsApi.deleteEvent(eventId);
      return eventId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const compileEvent = createAsyncThunk(
  'customEvents/compileEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      const response = await customEventsApi.compileEvent(eventId);
      return { eventId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchHistoricalFires = createAsyncThunk(
  'customEvents/fetchHistoricalFires',
  async ({ eventId, days = 30 }, { rejectWithValue }) => {
    try {
      const response = await customEventsApi.getHistoricalFires(eventId, days);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  events: [],
  eventsLoading: false,
  eventsError: null,

  currentEvent: null,
  currentEventLoading: false,
  currentEventError: null,

  filters: {
    limit: 50,
    offset: 0,
    tags: [],
    complexity: '',
    timeframe: ''
  },

  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isCompiling: false,

  historicalFires: [],
  historicalFiresLoading: false,
  historicalFiresError: null,

  successMessage: null
};

const customEventsSlice = createSlice({
  name: 'customEvents',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentEvent: (state) => {
      state.currentEvent = null;
      state.currentEventError = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearErrors: (state) => {
      state.eventsError = null;
      state.currentEventError = null;
    },
    clearHistoricalFires: (state) => {
      state.historicalFires = [];
      state.historicalFiresError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.eventsLoading = true;
        state.eventsError = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.eventsLoading = false;
        state.events = action.payload.events || action.payload;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.eventsLoading = false;
        state.eventsError = action.payload;
      })

      .addCase(fetchEvent.pending, (state) => {
        state.currentEventLoading = true;
        state.currentEventError = null;
      })
      .addCase(fetchEvent.fulfilled, (state, action) => {
        state.currentEventLoading = false;
        state.currentEvent = action.payload.event || action.payload;
      })
      .addCase(fetchEvent.rejected, (state, action) => {
        state.currentEventLoading = false;
        state.currentEventError = action.payload;
      })

      .addCase(createEvent.pending, (state) => {
        state.isCreating = true;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.isCreating = false;
        state.successMessage = 'Custom event created successfully!';
        // Use the full event returned by the backend if available
        if (action.payload.event) {
          state.currentEvent = action.payload.event;
        }
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.isCreating = false;
        state.eventsError = action.payload;
      })

      .addCase(updateEvent.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.successMessage = 'Custom event updated successfully!';

        // Use the full event returned by the backend if available
        const fullEvent = action.payload.event;
        if (fullEvent) {
          const index = state.events.findIndex(e => e.eventId === fullEvent.eventId);
          if (index !== -1) {
            state.events[index] = fullEvent;
          }
          if (state.currentEvent?.eventId === fullEvent.eventId) {
            state.currentEvent = fullEvent;
          }
        } else {
          // Fallback: merge sparse payload (updatedAt only) — avoids wiping visual
          const index = state.events.findIndex(e => e.eventId === action.payload.eventId);
          if (index !== -1) {
            state.events[index] = { ...state.events[index], ...action.payload };
          }
          if (state.currentEvent?.eventId === action.payload.eventId) {
            state.currentEvent = { ...state.currentEvent, ...action.payload };
          }
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.isUpdating = false;
        state.currentEventError = action.payload;
      })

      .addCase(deleteEvent.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.successMessage = 'Custom event deleted successfully!';
        state.events = state.events.filter(e => e.eventId !== action.payload);

        if (state.currentEvent?.eventId === action.payload) {
          state.currentEvent = null;
        }
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.isDeleting = false;
        state.eventsError = action.payload;
      });

    builder
      .addCase(compileEvent.pending, (state) => {
        state.isCompiling = true;
      })
      .addCase(compileEvent.fulfilled, (state, action) => {
        state.isCompiling = false;
        state.successMessage = 'Custom event compiled successfully!';

        const index = state.events.findIndex(e => e.eventId === action.payload.eventId);
        if (index !== -1) {
          state.events[index] = { ...state.events[index], ...action.payload };
        }

        if (state.currentEvent?.eventId === action.payload.eventId) {
          state.currentEvent = { ...state.currentEvent, ...action.payload };
        }
      })
      .addCase(compileEvent.rejected, (state, action) => {
        state.isCompiling = false;
        state.eventsError = action.payload;
      });

    builder
      .addCase(fetchHistoricalFires.pending, (state) => {
        state.historicalFiresLoading = true;
        state.historicalFiresError = null;
      })
      .addCase(fetchHistoricalFires.fulfilled, (state, action) => {
        state.historicalFiresLoading = false;
        state.historicalFires = action.payload.events || action.payload;
      })
      .addCase(fetchHistoricalFires.rejected, (state, action) => {
        state.historicalFiresLoading = false;
        state.historicalFiresError = action.payload;
      });
  }
});

export const { setFilters, clearCurrentEvent, clearSuccessMessage, clearErrors, clearHistoricalFires } =
  customEventsSlice.actions;

export default customEventsSlice.reducer;
